/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../../store/userSlice';
import { resetNavigation } from "../../../store/BASE/navigationSlice";
// actions
import { getListing } from "../../../actions";
// react hook form
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import BASESvgIcon from '../../../../@BASE/core/BASESvgIcon';
import BASELoading from '../../../../@BASE/core/BASELoading';
// material-ui
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const defaultValues = {
  first_name: "",
  last_name: "",
  email: "",
  organization: "",
  title: "",
};

const schema = yup.object().shape({
  first_name: yup.string().required("You must enter a name"),
  last_name: yup.string().required("You must enter a last name"),
  email: yup.string().required("You must enter a last name"),
  organization: yup.string().required("You must select an organization"),
  title: yup.string().required("You must enter a title"),
});

function Account() {
  const {user, organizations} = useOutletContext();
  const dispatch = useDispatch();
  // form
  const { control, watch, reset, handleSubmit, formState, getData, setValue } =
    useForm({
      mode: "onChange",
      resolver: yupResolver(schema),
      defaultValues,
    });
  // form state
  const { isValid, dirtyFields, errors } = formState;

  useEffect(() => {
    let data = {
      ...user,
      organization: user?.organization?._id || "",
    };
    reset({ ...data });
  }, [user, reset]);

  function onSubmit(data) {
		console.log(data);
	}

  if (!user || !organizations) {
    return <BASELoading />;
  }

  
  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
					<Typography className="text-xl">Personal Details</Typography>
					<Typography color="text.secondary">
						Manage visible user information, such as your name and email address.
					</Typography>
        </div>
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  placeholder="First Name"
                  id="first_name"
                  error={!!errors?.first_name}
                  helperText={errors?.first_name?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size='small'
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  placeholder="Last Name"
                  id="last_name"
                  error={!!errors?.last_name}
                  helperText={errors?.last_name?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size='small'
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  placeholder="Email"
                  variant="outlined"
                  required
                  fullWidth
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                  size='small'
                />
              )}
            />
          </div>
        </div>
        <div className="my-40 border-t" />
				<div className="w-full">
					<Typography className="text-xl">Organization Details</Typography>
					<Typography color="text.secondary">
						Manage your organization and job title.
					</Typography>
				</div>
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.organiztion}
              required
            >
              <InputLabel id="organiztion">Organization</InputLabel>
              <Controller
                control={control}
                name="organization"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Organization"
                    placeholder="Organization"
                    id="organization"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Select an Organization</MenuItem>
                    {organizations.map((organization) => (
                      <MenuItem value={organization.id} key={organization.id} className="space-x-12">
                        <span>{organization.name}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Job Title"
                  placeholder="Job Title"
                  variant="outlined"
                  required
                  fullWidth
                  error={!!errors.title}
                  helperText={errors?.title?.message}
                  size='small'
                />
              )}
            />
          </div>
        </div>
        <Divider className="mb-40 mt-44 border-t" />
        <div className="flex items-center justify-end space-x-8">
          <Button
            variant="outlined"
            disabled={_.isEmpty(dirtyFields)}
            onClick={() => {
              let data = {
                ...user,
                organization: user?.organization?._id ||"",
              };
              reset({ ...data });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={_.isEmpty(dirtyFields) || !isValid}
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}

export default Account;